import { SHOW_DEV_TOOLS } from "../config.js";

/** Dev-only controls: info panel + performance stats, country selector, IBL, path tracing. */
export class DevToolsPanel {
  constructor(app, uiManager) {
    this.app = app;
    this.ui = uiManager;

    // Performance tracking
    this.performanceStats = {
      fps: 60,
      frameTime: 0,
      furnitureCount: 0,
      memoryUsage: 0,
    };
  }

  // Dev-only toggles in the settings overlay (controls hidden otherwise)
  setupDevToggles() {
    if (!SHOW_DEV_TOOLS) return;

    // Disable collision — module-vs-module tests only; room bounds still apply
    const disableCollisionControl = document.getElementById("disable-collision-control");
    if (disableCollisionControl) disableCollisionControl.style.display = "";
    const disableCollisionToggle = document.getElementById("disable-collision-toggle");
    if (disableCollisionToggle) {
      disableCollisionToggle.addEventListener("change", (e) => {
        const collisionSystem = this.app.getCollisionSystem();
        if (collisionSystem) collisionSystem.collisionEnabled = !e.target.checked;
        this.app.getStateManager()?.saveSceneState();
      });
    }

    const logCameraControl = document.getElementById("log-camera-control");
    if (logCameraControl) logCameraControl.style.display = "";
    const logCameraToggle = document.getElementById("log-camera-toggle");
    if (logCameraToggle) {
      logCameraToggle.addEventListener("change", (e) => {
        const cameraManager = this.app.getCameraManager();
        if (cameraManager) cameraManager.logCameraPosition = e.target.checked;
      });
    }

    // High-poly models — swaps the model directory and rebuilds the scene in place.
    const highPolyControl = document.getElementById("high-poly-control");
    const highPolyToggle = document.getElementById("high-poly-toggle");
    // ?highPoly= pins the choice for render batches, so the toggle would be a lie; leave it hidden.
    if (highPolyControl && this.app.getFurnitureManager()?.canToggleHighPoly()) {
      highPolyControl.style.display = "";
      highPolyToggle?.addEventListener("change", (e) => this.ui.setHighPoly(e.target.checked));
    }
  }

  trackFrame(frameTime) {
    if (this._perfMonitoring) this._trackPerformance(frameTime);
  }

  // Only the dev info panel reads the stats, so nothing is counted until it exists.
  startPerformanceMonitoring() {
    this._perfMonitoring = true;
    this.frameCount = 0;
    this.lastFPSTime = performance.now();
  }

  setupInfoPanel() {
    const infoPanel = document.createElement('div');
    infoPanel.id = 'info-panel';
    infoPanel.innerHTML = `
      <div id="fps-counter">FPS: 0</div>
      <div id="frame-time">Frame: 0ms</div>
      <div id="furniture-count">Furniture: 0</div>
      <div id="memory-usage">Memory: 0MB</div>
      <div id="pt-samples" style="display:none">PT Samples: 0</div>
    `;
    const devColumn = document.getElementById('dev-column');
    if (devColumn) {
      devColumn.classList.remove('hidden');
      devColumn.appendChild(infoPanel);
    }
    this.updateInfoPanel();
    this.startPerformanceMonitoring();
  }

  async setupCountrySelector() {
    if (!SHOW_DEV_TOOLS) {
      return; // Don't show in production
    }

    try {
      // Fetch country features
      const cartManager = this.app.getCartManager();
      const countryFeatures = await cartManager.fetchCountryFeatures();

      // Get current country from CartManager
      const currentCountry = cartManager.countryCode;

      // Inject country selector into the info panel (created by setupInfoPanel)
      const infoPanel = document.getElementById('info-panel');
      if (!infoPanel) {
        return;
      }

      const countrySelectorHTML = `
        <div id="country-selector" class="country-selector">
          <label for="country-select">Country:</label>
          <select id="country-select" class="country-select">
            <option value="">Auto Detect</option>
          </select>
        </div>
      `;

      infoPanel.insertAdjacentHTML('beforeend', countrySelectorHTML);

      // Get references to the newly created elements
      const countrySelect = document.getElementById('country-select');
      if (!countrySelect) {
        return;
      }

      // Sort countries by code
      const countries = Object.entries(countryFeatures).sort((a, b) => {
        return a[0].localeCompare(b[0]);
      });

      // Add countries to dropdown
      countries.forEach(([code]) => {
        const option = document.createElement('option');
        option.value = code;
        option.textContent = code;

        countrySelect.appendChild(option);
      });

      // Set current country
      const urlParams = new URLSearchParams(window.location.search);
      const urlCountry = urlParams.get('country');

      if (urlCountry) {
        countrySelect.value = urlCountry.toUpperCase();
      } else if (currentCountry) {
        countrySelect.value = currentCountry;
      }

      // Handle country change
      countrySelect.addEventListener('change', (e) => {
        const selectedCountry = e.target.value;

        if (selectedCountry) {
          // Reload with country parameter
          const url = new URL(window.location);
          url.searchParams.set('country', selectedCountry);
          window.location.href = url.toString();
        } else {
          // Remove country parameter
          const url = new URL(window.location);
          url.searchParams.delete('country');
          window.location.href = url.toString();
        }
      });

    } catch (error) {
      console.error('Failed to setup country selector:', error);
    }
  }

  // Dev-only IBL popover: A/B the baked IBL against the sky bake and tune it live.
  setupIblPopover() {
    if (!SHOW_DEV_TOOLS) return;

    const item = document.getElementById('ibl-item');
    const toggle = document.getElementById('ibl-toggle');
    const controls = document.getElementById('ibl-controls');
    const sourceSelect = document.getElementById('ibl-source');
    const gradientControls = document.getElementById('ibl-gradient-controls');
    const sceneManager = this.app.getSceneManager();

    if (!item || !toggle || !controls || !sourceSelect ||
        !gradientControls || !sceneManager) return;
    item.classList.remove('hidden');

    const sliders = [
      { id: 'ibl-intensity', gradientOnly: false,
        apply: (v) => sceneManager.setEnvironmentIntensity(v), read: () => sceneManager.environmentIntensity },
      { id: 'ibl-down', gradientOnly: true,
        apply: (v) => sceneManager.setIblGradient({ down: v }), read: () => sceneManager.iblGradient.down },
      { id: 'ibl-up', gradientOnly: true,
        apply: (v) => sceneManager.setIblGradient({ up: v }), read: () => sceneManager.iblGradient.up },
      { id: 'ibl-exponent', gradientOnly: true,
        apply: (v) => sceneManager.setIblGradient({ exponent: v }), read: () => sceneManager.iblGradient.exponent },
    ].map((entry) => ({
      ...entry,
      slider: document.getElementById(entry.id),
      readout: document.getElementById(`${entry.id}-value`),
    })).filter((entry) => entry.slider);

    const colorInputs = [
      { id: 'ibl-down-color', key: 'downColor' },
      { id: 'ibl-up-color', key: 'upColor' },
    ].map((entry) => ({ ...entry, input: document.getElementById(entry.id) }))
      .filter((entry) => entry.input);

    const syncEnabled = () => {
      const baked = sceneManager.iblMode !== 'sky';
      const gradient = sceneManager.iblMode === 'gradient';
      controls.style.opacity = baked ? '' : '0.4';
      gradientControls.style.opacity = gradient ? '' : '0.4';
      sourceSelect.disabled = !baked;
      sliders.forEach(({ slider, gradientOnly }) => {
        slider.disabled = gradientOnly ? !gradient : !baked;
      });
      colorInputs.forEach(({ input }) => { input.disabled = !gradient; });
    };

    toggle.checked = sceneManager.iblMode !== 'sky';
    toggle.addEventListener('change', (e) => {
      sceneManager.setIblMode(e.target.checked ? sourceSelect.value : 'sky');
      sceneManager.saveEnvironmentSettings();
      syncEnabled();
    });

    if (sceneManager.iblMode !== 'sky') sourceSelect.value = sceneManager.iblMode;
    sourceSelect.addEventListener('change', (e) => {
      sceneManager.setIblMode(e.target.value);
      sceneManager.saveEnvironmentSettings();
      syncEnabled();
    });

    sliders.forEach(({ slider, readout, apply, read }) => {
      const initial = read();
      slider.value = initial;
      if (readout) readout.textContent = initial.toFixed(2);
      slider.addEventListener('input', (e) => {
        const value = parseFloat(e.target.value);
        apply(value);
        if (readout) readout.textContent = value.toFixed(2);
      });
      // Persist on release rather than per drag frame.
      slider.addEventListener('change', () => sceneManager.saveEnvironmentSettings());
    });

    colorInputs.forEach(({ input, key }) => {
      input.value = `#${sceneManager.iblGradient[key].toString(16).padStart(6, '0')}`;
      input.addEventListener('input', (e) => {
        sceneManager.setIblGradient({ [key]: parseInt(e.target.value.slice(1), 16) });
      });
      input.addEventListener('change', () => sceneManager.saveEnvironmentSettings());
    });

    syncEnabled();
  }

  setupPathTracingToggle() {
    if (!SHOW_DEV_TOOLS) {
      return; // Don't show in production
    }

    const btn = document.getElementById('path-tracing-btn');
    if (!btn) return;
    btn.classList.remove('hidden');
    btn.title = `${this.ui.t('designerSettingsPathTracing', 'Path Tracing')} (P)`;

    btn.addEventListener('click', async () => {
      if (btn.classList.contains('busy')) return;

      const enable = !btn.classList.contains('active');

      // Path tracing is 3D-only
      if (enable && this.app.getCameraManager()?.getSceneMode() === '2D') return;

      btn.classList.add('busy');
      try {
        // Lazily load the path-tracer chunk if the idle warm-up hasn't run yet.
        const ptManager = await this.app.ensurePathTracingManager();
        if (!ptManager) return;

        await ptManager.setEnabled(enable);
        if (enable && !ptManager.isSupported()) {
          this.ui.showNotification(
            this.ui.t("designerNotifyPathTracingUnsupported", "Path tracing needs WebGPU, which this browser doesn't support"),
            "warning",
          );
        }
        // buildScene failure resets enabled=false — reflect the real state
        this.updatePathTracingButton();
      } finally {
        btn.classList.remove('busy');
      }
    });
  }

  // Dev-only: opens the path-tracing light rig console (also on L).
  setupLightRigConsole() {
    if (!SHOW_DEV_TOOLS) return;

    const btn = document.getElementById('light-rig-btn');
    if (!btn) return;
    btn.classList.remove('hidden');
    // The console owns the button's lit state — it can also be closed by L or its ✕.
    btn.addEventListener('click', () => {
      this.app.ensureLightRigConsole().then((rigConsole) => rigConsole?.toggle());
    });
  }

  updatePathTracingButton() {
    const btn = document.getElementById('path-tracing-btn');
    if (!btn) return;

    const active = this.app.getPathTracingManager()?.isActive() ?? false;
    btn.classList.toggle('active', active);

    const is2D = this.app.getCameraManager()?.getSceneMode() === '2D';
    btn.disabled = is2D;

    // Dimensions are suppressed while tracing — reflect that in the settings toggle
    // instead of leaving a checkbox that silently does nothing.
    const dimensionsToggle = document.getElementById('dimensions-toggle');
    if (dimensionsToggle) dimensionsToggle.disabled = active;
  }

  updatePerformanceStats() {
    if (!this._perfMonitoring) return;
    const furnitureManager = this.app.getFurnitureManager();
    const materialManager = this.app.getMaterialManager();

    if (furnitureManager) {
      this.performanceStats.furnitureCount =
        furnitureManager.getAllFurniture().length;
    }

    if (materialManager) {
      this.performanceStats.memoryUsage =
        materialManager.getStats().memoryUsage;
    }
  }

  updateInfoPanel() {
    const fpsCounter = document.getElementById("fps-counter");
    const frameTime = document.getElementById("frame-time");
    const furnitureCount = document.getElementById("furniture-count");
    const memoryUsage = document.getElementById("memory-usage");
    const ptSamples = document.getElementById("pt-samples");

    if (fpsCounter) {
      fpsCounter.textContent = `FPS: ${this.performanceStats.fps}`;
    }

    if (frameTime) {
      frameTime.textContent = `Frame: ${this.performanceStats.frameTime.toFixed(1)}ms`;
    }

    if (furnitureCount) {
      furnitureCount.textContent = `Furniture: ${this.performanceStats.furnitureCount}`;
    }

    if (memoryUsage) {
      memoryUsage.textContent = `Memory: ${this.performanceStats.memoryUsage}MB`;
    }

    if (ptSamples) {
      const ptManager = this.app.getPathTracingManager();
      if (ptManager?.isActive()) {
        ptSamples.style.display = "";
        // The tracer counts samples fractionally while a pass is in flight; only completed ones are worth showing.
        ptSamples.textContent = `PT Samples: ${Math.floor(ptManager.getSampleCount())}`;
      } else {
        ptSamples.style.display = "none";
      }
    }
  }

  _trackPerformance(frameTime) {
    this.frameCount++;
    const currentTime = performance.now();

    // Update frame time (moving average for smoothing)
    if (frameTime !== undefined) {
      this.performanceStats.frameTime = this.performanceStats.frameTime * 0.9 + frameTime * 0.1;
    }

    // Update FPS once per second
    if (currentTime - this.lastFPSTime >= 1000) {
      this.performanceStats.fps = Math.round(
        (this.frameCount * 1000) / (currentTime - this.lastFPSTime),
      );
      this.frameCount = 0;
      this.lastFPSTime = currentTime;

      // Only update UI once per second instead of every frame
      this.updatePerformanceStats();
      this.updateInfoPanel();
    }
  }
}
